/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { ReactElement, ReactNode } from "react"
import { Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import { VIEWER_UPSTREAM_VERSION } from "./upstream-versions"

/**
 * The viewer's two information dialogs, in the handoff viewer's modal style
 * (upstream v0.9.1 `pcgHelpModal` / `pcgAboutModal`): "Viewer Quick Start",
 * opened from the Quick Start button in the selected-recording head and from
 * the brand menu, and "About Medaica Viewer", opened from the brand menu.
 * Wording is verbatim from upstream, in its two variants: without an
 * Evaluation Range (results come from the usable portion of the recording)
 * and, with `hasEvaluationRange` (the timing engine selected a range for the
 * recording), with upstream's Evaluation Range sentences. Each closes on its
 * close button, Escape or a backdrop click, as upstream's do.
 */

/** A step; `lead` is the bold opening words. */
interface QuickStartStep {
  lead: string
  text: string
}

// Steps 3 and 4 (upstream `visualGuideRangeCopy` / `provisionalResultBasisCopy`)
// depend on whether the recording has an Evaluation Range.
const quickStartSteps = (hasEvaluationRange: boolean): QuickStartStep[] => [
  {
    lead: "Select a recording.",
    text:
      " Use the body map, arrows or dots to move between recording positions. Filled markers have useful " +
      "evidence and hollow markers have limited evidence. Green rings mark accepted Report evidence, red rings " +
      "mark rejected recordings, and the current marker is white-filled. At a site with multiple recordings, the " +
      "small index shows the displayed recording; click the marker again to cycle. Hover over a marker for its " +
      "status.",
  },
  {
    lead: "Audio - listen and focus.",
    text:
      " Play the original recording and use the playback filters, volume, speed and loop controls to focus on " +
      "sounds of interest. Loop defaults to the current visible window when enabled. Playback filters change " +
      "only what you hear; they do not alter the source recording or calculated results.",
  },
  {
    lead: "Visual - inspect helpful guides.",
    text:
      " Turn on S1/S2, SYS/DIA or Energy to focus on visual features." +
      (hasEvaluationRange
        ? " Guides are strongest inside the Evaluation Range and fainter outside it for context."
        : "") +
      " Visual controls do not alter the source recording or calculated results.",
  },
  {
    lead: "Review the Provisional Results.",
    text:
      " " +
      (hasEvaluationRange
        ? "Results are calculated from the highlighted Evaluation Range; the rest of the waveform remains visible " +
          "as context."
        : "Results are calculated from the usable portion of the recording.") +
      " Provisional results are review aids, not diagnostic.",
  },
  {
    lead: "Record your decision.",
    text:
      " Select Accept and choose the evidence suitable for Medaica Report, or Reject the recording and optionally " +
      "add a note. Export when the required recordings have been reviewed; only accepted evidence is carried into " +
      "the report.",
  },
]

/** The shared dialog frame: a titled MUI dialog with a close button in its head. */
const PcgViewerModal = ({
  open,
  onClose,
  titleId,
  title,
  closeLabel,
  children,
}: {
  open: boolean
  onClose: () => void
  titleId: string
  title: string
  closeLabel: string
  children: ReactNode
}): ReactElement => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper" aria-labelledby={titleId}>
    <DialogTitle id={titleId} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}>
      {title}
      <IconButton aria-label={closeLabel} size="small" onClick={onClose}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </DialogTitle>
    <DialogContent dividers>{children}</DialogContent>
  </Dialog>
)

const PcgViewerHelpDialog = ({
  open,
  onClose,
  hasEvaluationRange = false,
}: {
  open: boolean
  onClose: () => void
  hasEvaluationRange?: boolean
}): ReactElement => (
  <PcgViewerModal
    open={open}
    onClose={onClose}
    titleId="pcg-viewer-help-title"
    title="Viewer Quick Start"
    closeLabel="Close quick-start guide"
  >
    <Typography
      component="ol"
      variant="body2"
      sx={{ m: 0, pl: 2.5, listStyle: "decimal", "& li": { display: "list-item", mb: 1 } }}
    >
      {quickStartSteps(hasEvaluationRange).map((step) => (
        <li key={step.lead}>
          <strong>{step.lead}</strong>
          {step.text}
        </li>
      ))}
    </Typography>
    <Typography variant="body2" mt={2}>
      <strong>Tips On / Tips Off</strong> controls brief explanations for buttons, controls and body-map markers. Tips
      are on by default and can be switched off when the interface is familiar. <strong>Waveform data</strong> is
      separate: it appears when you hover over the waveform with a relevant Visual guide selected, even when tips are
      off.
    </Typography>
  </PcgViewerModal>
)

/**
 * "About Medaica Viewer" (upstream v0.9.1 `pcgAboutModal`): the version line,
 * whose hover names the absorbed upstream commit, the description, and the
 * results basis. With `hasEvaluationRange` (the timing engine produced the
 * displayed Evaluation Range) it adds upstream's timing-engine line and words
 * the results basis for the range.
 */
const PcgViewerAboutDialog = ({
  open,
  onClose,
  hasEvaluationRange = false,
}: {
  open: boolean
  onClose: () => void
  hasEvaluationRange?: boolean
}): ReactElement => (
  <PcgViewerModal
    open={open}
    onClose={onClose}
    titleId="pcg-viewer-about-title"
    title="About Medaica Viewer"
    closeLabel="Close about dialog"
  >
    <Typography variant="body2">
      <strong title={VIEWER_UPSTREAM_VERSION.source} data-testid="pcg-viewer-about-version">
        Medaica Viewer v{VIEWER_UPSTREAM_VERSION.displayVersion}
      </strong>
    </Typography>
    <Typography variant="body2" mt={1.5}>
      Review recorded heart sounds and choose accepted evidence for Medaica Report.
    </Typography>
    {hasEvaluationRange ? (
      <Typography variant="body2" mt={1.5}>
        <strong>Timing engine:</strong> Springer/m1acq timing produced the displayed Evaluation Range.
      </Typography>
    ) : null}
    <Typography variant="body2" mt={1.5}>
      <strong>Results basis:</strong>{" "}
      {hasEvaluationRange
        ? "provisional results are calculated from the highlighted Evaluation Range; the full waveform remains " +
          "visible for context."
        : "provisional results are calculated from the usable portion of the recording."}
    </Typography>
  </PcgViewerModal>
)

export default PcgViewerHelpDialog
export { PcgViewerAboutDialog }
